/**
 * Maszyna stanów reconnect §6.4 — czysta logika (unit-testowalna).
 * Backoff 1/2/4/8/15 s, okno max 120 s (RECONNECT_WINDOW_SEC).
 */
import { LEAVE_CODE_SESSION_TAKEN, RECONNECT_WINDOW_SEC } from '@pieskowo/shared';

export const RECONNECT_BACKOFF_MS = [1000, 2000, 4000, 8000, 15000] as const;
export const RECONNECT_WINDOW_MS = RECONNECT_WINDOW_SEC * 1000;

/** Status połączenia widoczny dla UI (overlay „śpiący piesek”). */
export type NetPhase =
  | 'idle'
  | 'allocating'
  | 'joining'
  | 'connected'
  | 'reconnecting'
  | 'session_taken'
  | 'offline'
  | 'error';

export type LeaveClass = 'consented' | 'session_taken' | 'disconnect';

/**
 * Klasyfikacja kodu leave Colyseus.
 * 4001 = SESSION_TAKEN; 1000 / consented = świadome wyjście; reszta = flap.
 */
export function classifyLeave(code: number, consented = false): LeaveClass {
  if (code === LEAVE_CODE_SESSION_TAKEN) return 'session_taken';
  if (consented || code === 1000) return 'consented';
  return 'disconnect';
}

/** Opóźnienie backoffu dla n-tej próby (0-based). */
export function nextBackoffMs(attempt: number): number {
  const i = Math.max(0, Math.min(attempt, RECONNECT_BACKOFF_MS.length - 1));
  return RECONNECT_BACKOFF_MS[i]!;
}

/** Czy jeszcze próbujemy reconnect w oknie 120 s. */
export function shouldRetryReconnect(
  elapsedMs: number,
  windowMs: number = RECONNECT_WINDOW_MS,
): boolean {
  return elapsedMs < windowMs;
}

/**
 * Decyzja po nieudanym `client.reconnect(token)`:
 * - w oknie → kolejna próba z backoffem
 * - po oknie / token martwy → reallocate (świeży joinById)
 */
export type ReconnectDecision =
  | { action: 'retry'; attempt: number; delayMs: number }
  | { action: 'reallocate' }
  | { action: 'stop_session_taken' };

export function decideAfterReconnectFailure(opts: {
  elapsedMs: number;
  attempt: number;
  /** true gdy błąd to SESSION_TAKEN / 4001 */
  sessionTaken?: boolean;
  /** true gdy token wygasł / pokój zamknięty — od razu reallocate */
  tokenDead?: boolean;
  windowMs?: number;
}): ReconnectDecision {
  if (opts.sessionTaken) return { action: 'stop_session_taken' };
  if (opts.tokenDead) return { action: 'reallocate' };
  if (!shouldRetryReconnect(opts.elapsedMs, opts.windowMs)) {
    return { action: 'reallocate' };
  }
  const attempt = opts.attempt;
  return {
    action: 'retry',
    attempt,
    delayMs: nextBackoffMs(attempt),
  };
}

/** Następna faza UI po sklasyfikowanym leave. */
export function phaseAfterLeave(leave: LeaveClass): NetPhase {
  switch (leave) {
    case 'session_taken':
      return 'session_taken';
    case 'consented':
      return 'idle';
    case 'disconnect':
      return 'reconnecting';
  }
}
